import type { Theme } from '@material-ui/core/styles'

/**
 * The redesigned estimate editor's type scale (ENG-2094), one entry per role in the spec. Sizes are
 * in px through `pxToRem`, so they do not follow the account's font scale the way the theme's
 * Typography variants do: the same role reads at the same size on every account.
 *
 * Spread a role into a `makeStyles` rule (`title: { ...nextType.cardTitle }`) and override what the
 * surface needs, e.g. the colour on a dark band. Buttons are not here; they take `theme.typography.button`.
 */
export function getNextTypography(theme: Theme) {
  const { pxToRem } = theme.typography
  const { Neutral } = theme.paintscout.palette.brand
  const text = Neutral.P35
  const label = Neutral.P60

  return {
    /** 28/600: the contact name at the top of the editor. */
    pageTitle: { fontSize: pxToRem(28), fontWeight: 600, color: text },
    /** 18/500: the company name and "Estimate" on the document. */
    documentTitle: { fontSize: pxToRem(18), fontWeight: 500, color: text },
    /** 16/500: table sections (Item, Payments, Options) and sheet titles. */
    sectionTitle: { fontSize: pxToRem(16), fontWeight: 500, color: text },
    /** 15/500 with 0.02em tracking: sidebar card titles (Details, Calculations, Tiered Packages). */
    cardTitle: { fontSize: pxToRem(15), fontWeight: 500, letterSpacing: '0.02em', color: text },
    /** 15/500: line items and their prices. */
    rowTitle: { fontSize: pxToRem(15), fontWeight: 500, color: text },
    /** 14/400: addresses, notes, values. */
    body: { fontSize: pxToRem(14), fontWeight: 400, color: text },
    /** 14/400 in the label colour: Status, Type, Sales Rep. */
    rowLabel: { fontSize: pxToRem(14), fontWeight: 400, color: label },
    /** 13/400 with 0.02em tracking: Prepared for, Job Address, Prepared by. */
    fieldLabel: { fontSize: pxToRem(13), fontWeight: 400, letterSpacing: '0.02em', color: label },
    /** 12/400: activity and chat times, drawer subtitles. */
    meta: { fontSize: pxToRem(12), fontWeight: 400, color: label }
  } as const
}

export type NextTypography = ReturnType<typeof getNextTypography>
